import {api} from '@/shared/services/api-client';
import {paginateAll} from '@/shared/services/paginateAll';
import {PARTICIPANTS_LISTALL_PAGE_SIZE} from '@/config/pagination';
import type {BulkInviteScopeFilters} from '@/shared/types/bulkInvite';
import type {
  CreateParticipantRemovalRequest,
  ParticipantRemovalsQuery,
  ParticipantRemovalsResponse,
} from '../types/participantRemovals';

export interface SendBookingLinkRequest {
  project_id: string;
  session_type_id?: string;
  moderator_id?: string;
  moderator_ids?: string[];
  session_type_ids?: string[];
  expires_at?: string;
  display_time_zone?: string;
  custom_message?: string;
  cancel_active_schedules?: boolean;
  /**
   * OD-1325: the meeting point to send this invite for. Omit to use the session
   * type's primary — which is what every caller predating secondary meeting
   * points does, so their behaviour is unchanged. Only a configured, active
   * meeting point on that session type is accepted; there is no free-text path.
   */
  location_id?: number;
}

/**
 * Send self-booking links to many participants for ONE session type at once.
 * Two modes: explicit `participant_ids`, or `select_all_matching` (server resolves
 * the eligible set for the session type + current search, minus `excluded_participant_ids`).
 */
export interface BulkSendBookingLinkRequest extends BulkInviteScopeFilters {
  project_id: string;
  session_type_id: string;
  participant_ids?: string[];
  select_all_matching?: boolean;
  excluded_participant_ids?: string[];
  search?: string;
  expires_at?: string;
  display_time_zone?: string;
  custom_message?: string;
  /**
   * OD-1325: the meeting point to send this invite for. Omit to use the session
   * type's primary — which is what every caller predating secondary meeting
   * points does, so their behaviour is unchanged. Only a configured, active
   * meeting point on that session type is accepted; there is no free-text path.
   */
  location_id?: number;
  // BulkInviteScopeFilters fields above (Scheduler-queue filter scope) are
  // re-applied server-side when select_all_matching is true, so "Select all"
  // sends only to the filtered set.
}

export interface BulkSendBookingLinkResponse {
  success: boolean;
  mode: 'explicit' | 'all_matching';
  summary: {
    requested: number;
    sent: number;
    failed: number;
    skipped: number;
    /** Present only in all_matching mode. */
    total_matching?: number;
    remaining?: number;
  };
  sent: Array<{participant_id: string; name?: string | null; link_id: string}>;
  failed: Array<{participant_id: string; name?: string | null; reason: string}>;
  skipped: Array<{participant_id: string; name?: string | null; reason: string}>;
}

/**
 * Fetch *every* participant for a project by paging through the backend list endpoint
 * (which is hard-paginated). Returns the same shape as a single `list` call so callers
 * can drop this in wherever they used to fetch one big page with `limit: 500/1000`.
 *
 * Pages are fetched sequentially (not in parallel) to avoid hammering the backend on
 * very large projects. Throws on the first failed page so callers can show an error
 * and avoid silently rendering partial data.
 */
/**
 * Fetch every participant by paging through the backend list endpoint.
 * Pass a `projectId` to scope to one project; pass null/undefined to fetch
 * across all projects (used by cross-project screens like Journal Entry).
 * Pages are fetched sequentially to avoid hammering the backend on large
 * projects. Throws on the first failed page so callers can surface an error
 * instead of silently rendering a partial list.
 */
const listAllParticipants = async (
  projectId: string | null | undefined,
  pageSize: number = PARTICIPANTS_LISTALL_PAGE_SIZE,
): Promise<{
  data: {
    participants: any[];
    pagination: {total: number; pages: number; page: number; limit: number};
  };
}> => {
  const params: Record<string, unknown> = {};
  if (projectId) params.project_id = projectId;

  const {rows, pagination} = await paginateAll(
    (p) => api.participants.list(p),
    params,
    'participants',
    pageSize,
  );

  return {data: {participants: rows, pagination}};
};

export const participantsApi = {
  list: (params?: any) => api.participants.list(params),
  /**
   * Fetch every participant — for a project when `projectId` is provided, or
   * across all projects when it is null/undefined. Pages through the backend
   * internally so callers never need a hardcoded `limit`.
   */
  listAll: (projectId?: string | null, pageSize?: number) =>
    listAllParticipants(projectId, pageSize),
  get: (id: string, projectId?: string) => api.participants.get(id, projectId),
  getByEmail: (email: string) => api.participants.getByEmail(email),
  create: (data: any) => api.participants.create(data),
  createBulk: (data: any) => api.participants.createBulk(data),
  update: (id: string, data: any) => api.participants.update(id, data),
  delete: (id: string) => api.participants.delete(id),
  resetPassword: (id: string) => api.participants.resetPassword(id),
  removeFromProject: (id: string, projectId: string) =>
    api.participants.removeFromProject(id, projectId),
  createRemoval: (
    id: string,
    projectId: string,
    data: CreateParticipantRemovalRequest,
  ) => api.participants.createRemoval(id, projectId, data),
  listRemovalHistory: (projectId: string, params?: ParticipantRemovalsQuery) =>
    api.participants.listRemovalHistory(projectId, params) as Promise<{
      data: ParticipantRemovalsResponse;
    }>,
  exportRemovals: (
    projectId: string,
    params?: Omit<ParticipantRemovalsQuery, 'page' | 'limit'>,
  ) => api.participants.exportRemovals(projectId, params),
  assignScheduler: (data: any) => api.participants.assignScheduler(data),
  sendBookingLink: (participantId: string, data: SendBookingLinkRequest) =>
    api.participants.sendBookingLink(participantId, data),
  bulkSendBookingLink: (data: BulkSendBookingLinkRequest) =>
    api.participants.bulkSendBookingLink(data),
  statusCards: (projectId: string) => api.participants.statusCards(projectId),
  filterOptions: (projectId: string, options?: {refresh?: boolean}) =>
    api.participants.filterOptions(projectId, options),
  demographicsTable: (
    projectId: string,
    options?: {page?: number; limit?: number; refresh?: boolean},
  ) => api.participants.demographicsTable(projectId, options),
  columnValues: (projectId: string, columns: string[], options?: {refresh?: boolean}) =>
    api.participants.columnValues(projectId, columns, options),
  projectParticipantDemographicAnswers: (
    projectId: string,
    participantId: string,
  ) => api.participants.projectParticipantDemographicAnswers(projectId, participantId),
  projectParticipantSubmitRelationshipContext: (
    projectId: string,
    participantId: string,
    taskId?: string | null,
    scheduleId?: string | null,
  ) =>
    api.participants.projectParticipantSubmitRelationshipContext(
      projectId,
      participantId,
      taskId,
      scheduleId,
    ),
  getContactAttempts: (id: string, projectId: string) =>
    api.participants.getContactAttempts(id, projectId),
  logContactAttempt: (
    id: string,
    data: {project_id: string; outcome: string; notes?: string},
  ) => api.participants.logContactAttempt(id, data),
  updateScheduleStatus: (
    id: string,
    data: {project_id: string; schedule_status: string},
  ) => api.participants.updateScheduleStatus(id, data),
  getHistory: (id: string, projectId: string, scheduleId?: string) =>
    api.participants.getHistory(id, projectId, scheduleId),
  noteCounts: (projectId: string, participantIds: string[]) =>
    api.participants.noteCounts(projectId, participantIds),
  addNote: (id: string, data: {project_id: string; note: string}) =>
    api.participants.addNote(id, data),
  getUploadSchema: (projectId: string) =>
    api.participants.getUploadSchema(projectId),
};
